import React from "react";
import { useTranslation } from "react-i18next";
import type {
  TransformedCartTotals,
  TransformedCartCoupon, // Import the coupon type
} from "../../../../cart/types/cartApiTypes";

interface MiniCartTotalsProps {
  totals: TransformedCartTotals;
  coupons: TransformedCartCoupon[]; // Add coupons prop
  currencySymbol: string;
  itemCount: number;
  needsShipping: boolean;
  hasCalculatedShipping: boolean;
  isFreeShipping: boolean;
}

const MiniCartTotals: React.FC<MiniCartTotalsProps> = ({
  totals,
  coupons, // Destructure coupons
  currencySymbol,
  itemCount,
  needsShipping,
  hasCalculatedShipping,
  isFreeShipping,
}) => {
  const { t } = useTranslation();
  const isEmpty = itemCount === 0;

  // Check if there is a discount to display
  const hasDiscount = totals.totalDiscount > 0;

  return (
    <div className="mini-cart-totals">
      {/* Item Subtotal */}
      {!isEmpty && (
        <div className="mini-cart-totals__item">
          <span>{t("cart.totals.subtotal", "Subtotal")}:</span>
          <span>
            {totals.formattedTotalItems}
            {currencySymbol}
          </span>
        </div>
      )}

      {/* Shipping Cost */}
      {!isEmpty && needsShipping && (
        <div className="mini-cart-totals__item">
          <span className={isFreeShipping ? "free-shipping-label" : ""}>
            {isFreeShipping
              ? t("cart.totals.freeShipping", "Free Shipping")
              : t("cart.totals.otherShipping", "Shipping")}
            :
          </span>
          <span>
            {hasCalculatedShipping
              ? `${totals.formattedTotalShipping}${currencySymbol}`
              : t("cart.shippingCalculating", "Calculating...")}
          </span>
        </div>
      )}

      {/* START: Added Discount Display */}
      {!isEmpty && hasDiscount && (
        <div className="mini-cart-totals__item discount-item">
          <span>{t("cart.discount", "Discount")}:</span>
          <span>
            -{totals.formattedTotalDiscount}
            {currencySymbol}
          </span>
        </div>
      )}
      {/* END: Added Discount Display */}

      {/* Grand Total */}
      {!isEmpty && (
        <div className="mini-cart-totals__item grand-total">
          <strong>{t("cart.totals.total", "Total")}:</strong>
          <strong>
            {totals.formattedTotalPrice}
            {currencySymbol}
          </strong>
        </div>
      )}

      {/* Action Buttons */}
      <div className="mini-cart-totals__actions">
        <a
          href="/cart"
          className="button button--secondary mini-cart-totals__view-cart-button"
        >
          {t("cart.viewCart", "View Cart")}
        </a>
        <a
          href="/checkout"
          className={`button mini-cart-totals__checkout-button ${
            isEmpty ? "disabled" : ""
          }`}
          aria-disabled={isEmpty}
          onClick={(e) => {
            if (isEmpty) e.preventDefault();
          }} // Prevent click if disabled
        >
          {t("cart.proceedToCheckout", "Proceed to Checkout")}
        </a>
      </div>
    </div>
  );
};

export default MiniCartTotals;
